import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayPrice } from "@majourneys/currency";
import type { StayRow, StayShelf } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { LayoutButton, LayoutLabel } from "./layout-parts";
import { SOFT } from "./soft-tokens";
import { panelStyle } from "../../../shared/ui/card-frame";

/**
 * Somewhere to sleep, under the reply (owner, 2026-10-05: design B, a compact
 * list). Three rows in one card; each opens the hotel with the guest's dates
 * and party already set, where rooms, prices and the online payment live.
 * Stays are paid in full online, unlike everything else we arrange, and the
 * card says so. A dated list shows the whole stay's price; an undated one
 * shows none and asks for dates rather than inventing a "from".
 *
 * Chat kit C12: one framed card, a row per stay with its photo, its star
 * rating (stays are the only chat cards that carry one), the price and a
 * small "Paid online" under it on EVERY row, so no row can be read as pay on
 * arrival. Prices are whatever the shelf sends; member pricing is decided by
 * the API and not touched here.
 */

function query(shelf: StayShelf): string {
  if (!shelf.checkin || !shelf.checkout) return "";
  const q = new URLSearchParams({ checkin: shelf.checkin, checkout: shelf.checkout });
  if (shelf.adults) q.set("adults", String(shelf.adults));
  return `?${q.toString()}`;
}

export function stayHref(shelf: StayShelf, row: StayRow, lang: string): string {
  return `/${lang}/stays/${shelf.citySlug}/${row.slug}${query(shelf)}`;
}

export function staysCityHref(shelf: StayShelf, lang: string): string {
  return `/${lang}/stays/${shelf.citySlug}${query(shelf)}`;
}

function Stars({ n }: { n: number }) {
  const { t } = useTranslation();
  return (
    <span
      role="img"
      aria-label={t("ai_concierge.stays.stars", { count: n })}
      className="tracking-[1px]"
      style={{ color: SOFT.gold }}
    >
      {"★".repeat(Math.max(0, Math.min(5, Math.round(n))))}
    </span>
  );
}

export function StaysList({ shelf, lang }: { shelf: StayShelf; lang: ChatLang }) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const dated = Boolean(shelf.checkin && shelf.checkout);
  const fmtDay = (iso: string) =>
    new Intl.DateTimeFormat(`${lang}-u-nu-latn`, {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(new Date(`${iso}T12:00:00Z`));
  const heading = dated
    ? [
        t("ai_concierge.stays.eyebrow"),
        t("ai_concierge.proposal.stay_when", { from: fmtDay(shelf.checkin!), to: fmtDay(shelf.checkout!) }),
        ...(shelf.adults ? [t("ai_concierge.proposal.adults", { count: shelf.adults })] : []),
      ].join(" · ")
    : t("ai_concierge.stays.eyebrow");

  return (
    <section
      style={{ ...panelStyle(18, "#fff"), overflow: "hidden" }}
      aria-label={t("ai_concierge.stays.eyebrow")}
      data-testid="stays-list"
    >
      <div className="px-3 pt-3">
        <LayoutLabel>{heading}</LayoutLabel>
      </div>
      <ul className="mt-1">
        {shelf.items.map((row, i) => (
          <li key={row.slug} style={i > 0 ? { borderTop: `1px solid ${SOFT.hairline}` } : undefined}>
            <a
              href={stayHref(shelf, row, lang)}
              className="flex min-h-[64px] min-w-0 items-center gap-3 px-3 py-2.5 hover:bg-[rgba(17,14,57,0.03)]"
              data-testid={`stay-row-${row.slug}`}
            >
              <span
                className="flex h-[54px] w-[54px] shrink-0 items-center justify-center overflow-hidden rounded-[12px]"
                style={{ background: SOFT.sunken }}
              >
                {row.image ? (
                  <img
                    src={row.image}
                    alt=""
                    width={54}
                    height={54}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="line-clamp-2 block break-words text-[13.5px] font-semibold" style={{ color: SOFT.ink }}>
                  {row.title}
                </span>
                <span className="block text-[12px]" style={{ color: SOFT.body }}>
                  {row.starRating ? (
                    <>
                      <Stars n={row.starRating} />
                      {" · "}
                    </>
                  ) : null}
                  {shelf.city}
                </span>
              </span>
              <span className="shrink-0 text-end">
                {dated && row.totalPrice != null ? (
                  <>
                    <span
                      className="block whitespace-nowrap text-[14px] font-semibold tabular-nums"
                      style={{ color: SOFT.ink }}
                    >
                      {displayPrice(row.totalPrice)}
                    </span>
                    <span className="block text-[11px]" style={{ color: SOFT.body }}>
                      {t("ai_concierge.stays.total")}
                    </span>
                  </>
                ) : (
                  <span className="block max-w-[96px] text-[11.5px] leading-[15px]" style={{ color: SOFT.body }}>
                    {dated ? t("ai_concierge.stays.no_room") : t("ai_concierge.stays.add_dates")}
                  </span>
                )}
                <span
                  className="mt-0.5 inline-flex items-center gap-1 text-[10.5px]"
                  style={{ color: SOFT.body }}
                  data-testid="stay-paid-online"
                >
                  <Icon name="ri-lock-line" size={10} aria-hidden="true" />
                  {t("ai_concierge.stays.paid_online_short", { defaultValue: "Paid online" })}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
      <div className="px-3 pb-3 pt-2.5" style={{ borderTop: `1px solid ${SOFT.hairline}` }}>
        <p className="text-start text-[12.5px]" style={{ color: SOFT.body }}>
          {t("ai_concierge.stays.paid_online")}
        </p>
        <div className="mt-2.5 flex">
          <LayoutButton href={staysCityHref(shelf, lang)} primary>
            {dated ? t("ai_concierge.stays.see_all_dates") : t("ai_concierge.stays.see_all_city", { city: shelf.city })}
          </LayoutButton>
        </div>
      </div>
    </section>
  );
}
